import React from 'react';
import { Scroll, Building2, Wrench, Users, Heart, Check, Quote } from 'lucide-react';

const SERVICE_GROUPS = [
  {
    icon: Building2,
    title: 'Development Services',
    items: ['Land survey and assessment', 'Civil engineering', 'Architectural design', 'Site planning'],
  },
  {
    icon: Scroll,
    title: 'Promotion and Branding',
    items: [
      'Marketing strategy development',
      'Design and branding',
      'Website development',
      'Public relations and advocacy',
    ],
  },
  {
    icon: Wrench,
    title: 'Maintenance and Management',
    items: [
      'Landscaping and groundskeeping',
      'Record keeping and management',
      'Site inventory and mapping',
      'Pre-planning services',
      'Restoration and preservation',
    ],
  },
];

const VALUES = [
  {
    icon: Heart,
    title: 'Compassionate Care',
    description:
      'We approach every service with empathy, understanding, and a deep respect for the unique needs of each family.',
  },
  {
    icon: Users,
    title: 'Community Focus',
    description:
      'Building strong relationships within our communities to provide comprehensive support and services to families and religious communities.',
  },
  {
    icon: Building2,
    title: 'Professional Excellence',
    description:
      'Maintaining the highest standards in cemetery development and maintenance through innovative solutions.',
  },
];

const About = () => (
  <div className="bg-white">
    {/* Hero */}
    <div className="relative h-[440px]">
      <img
        src="/images/about/17.jpg"
        alt="A serene memorial garden"
        className="absolute h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-950/70 to-navy-950/80" />
      <div className="container relative mx-auto flex h-full flex-col items-center justify-center px-4 text-center">
        <p className="eyebrow animate-fade-up text-navy-100">Who we are</p>
        <h1 className="mt-4 max-w-3xl animate-fade-up text-4xl leading-tight text-white md:text-5xl">
          About Cemetery &amp; Burial Solutions
        </h1>
        <p className="mt-5 max-w-2xl animate-fade-up font-montserrat leading-relaxed text-slate-200 md:text-lg">
          Setting new standards in memorial services with dignity, respect, and innovation.
        </p>
      </div>
    </div>

    {/* Our Promise — a proper pull-quote instead of a stray italic line. */}
    <section className="border-b border-slate-200 bg-slate-50 py-20">
      <div className="container mx-auto px-4">
        <figure className="mx-auto max-w-3xl text-center">
          <Quote className="mx-auto h-9 w-9 text-navy-300" aria-hidden="true" />
          <blockquote className="mt-5 font-cormorant text-2xl font-medium leading-relaxed text-navy-900 md:text-[1.75rem]">
            “At Cemetery &amp; Burial Solutions, we offer a comprehensive suite of services designed
            to develop and maintain cemeteries with respect, sustainability, and a strong sense of
            community connection.”
          </blockquote>
        </figure>
      </div>
    </section>

    {/* Who We Are */}
    <section className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow">Our approach</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Who We Are</h2>
            <p className="mt-6 font-montserrat leading-relaxed text-slate-600">
              From initial planning and design to long-term care and community engagement, our
              tailored individual approach ensures that every detail is handled with
              professionalism and compassion.
            </p>
            <p className="mt-4 font-montserrat leading-relaxed text-slate-600">
              Through innovative memorial services, guided by compassion and a commitment to
              honoring the uniqueness of each life, we provide solace and inspire reflection.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-card">
            <img
              src="/images/about/8.jpg"
              alt="Groundskeepers tending a cemetery lawn"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Core Values */}
    <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow">What guides us</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Our Core Values</h2>
          <div className="rule mt-5" />
        </div>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {VALUES.map((value) => {
            const Icon = value.icon;
            return (
              <div
                key={value.title}
                className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
              >
                <span className="icon-chip transition-transform duration-300 group-hover:scale-110 group-hover:bg-navy-100">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-xl">{value.title}</h3>
                <p className="mt-3 font-montserrat text-sm leading-relaxed text-slate-600">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Our Mission — a clean two-column band, no more awkward inline image. */}
    <section className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">
          <div className="overflow-hidden rounded-2xl shadow-card md:order-1">
            <img
              src="/images/about/7.jpg"
              alt="A peaceful memorial garden in autumn light"
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
          <div>
            <p className="eyebrow">Our mission</p>
            <h2 className="mt-3 text-3xl md:text-4xl">A new standard for memorial care</h2>
            <p className="mt-6 font-montserrat leading-relaxed text-slate-600">
              Cemetery &amp; Burial Solutions&rsquo;s mission is to set new standards for the burial
              and memorial industry across the United States. Through our exceptional
              community-oriented memorial services we aim to safeguard and enhance the
              environmental, social, and governance (ESG) aspects of our sites, prioritizing
              responsible stewardship.
            </p>
            <p className="mt-4 font-montserrat leading-relaxed text-slate-600">
              Leveraging advanced technology, ESG principles, and a strategic business posture, we
              offer a comprehensive range of competitive and visionary services. Our scalable
              strategy ensures adaptability to market dynamics, positioning us as a leader in the
              evolving landscape of memorial services.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow">Full-service capability</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Our Services</h2>
          <div className="rule mt-5" />
        </div>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {SERVICE_GROUPS.map((group) => {
            const Icon = group.icon;
            return (
              <div
                key={group.title}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
              >
                <div className="mb-6 flex items-center gap-4">
                  <span className="icon-chip h-12 w-12">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="text-xl leading-snug">{group.title}</h3>
                </div>
                <ul className="space-y-3 border-t border-slate-100 pt-6">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-navy-600" />
                      <span className="font-montserrat text-sm text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  </div>
);

export default About;
